import { AfterViewInit, Component, OnDestroy, ViewChild, inject, DestroyRef } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, Subscription, merge, combineLatest } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, shareReplay } from 'rxjs/operators';
import { AuthorizationService } from '@bo-client/core/authorization/authorization.service';
import { DataShareService } from '@bo-client/shared/services/data-share.service';
import { NBPConfig } from '@bo-client/shared/config.model';
import { GridRegistrationModel } from '@bo-client/activation-management/grid-registration/model/grid-registration.model';
import { GridRegistrationFormBase } from '@bo-client/activation-management/grid-registration/grid-registration-form.base';
import { GridRegistrationTypeEnum } from '@bo-client/activation-management/grid-registration/model/grid-registration.enum';
import { GridRegistrationFormStorageV2Component } from '@bo-client/activation-management/grid-registrations/grid-registration-form-v2/grid-registration-form-storage-v2/grid-registration-form-storage-v2.component';
import { GridRegistrationFormConsumerComponent } from '@bo-client/activation-management/grid-registrations/grid-registration-form-v2/grid-registration-form-consumer/grid-registration-form-consumer.component';
import { StorageModel } from '@bo-client/activation-management/grid-registration/model/storage.model';

type StorageFormVariant = StorageModel['formVersion'];

function storageFormVariant(
  model: GridRegistrationModel | null,
  config: NBPConfig
): StorageFormVariant | null {
  if (!model || model.gridRegistrationType !== GridRegistrationTypeEnum.Storage) {
    return null;
  }
  if (model.id) {
    return !!model.storages?.length && model.storages.some((storage) => storage.formVersion === 2) ? 2 : 1;
  }
  return config?.gridRegistration?.storageConfig?.enableNewStorageProcess === true ? 2 : 1;
}

@Component({
  selector: 'app-grid-registration-form-storage-wrapper',
  templateUrl: './grid-registration-form-storage-wrapper.component.html',
  styleUrls: ['./grid-registration-form-storage-wrapper.component.scss']
})
export class GridRegistrationFormStorageWrapperComponent
  extends GridRegistrationFormBase
  implements AfterViewInit, OnDestroy
{
  private readonly destroyRef = inject(DestroyRef);
  private readonly model$ = new BehaviorSubject<GridRegistrationModel | null>(null);
  private lastModel: GridRegistrationModel | null = null;
  private lastOp: 'load' | 'update' = 'load';
  private lastChild: GridRegistrationFormBase | null = null;
  private childErrorsSub: Subscription | null = null;
  private readonly placeholderForm: FormGroup;

  private storageV2: GridRegistrationFormStorageV2Component | null = null;
  private consumer: GridRegistrationFormConsumerComponent | null = null;

  @ViewChild(GridRegistrationFormStorageV2Component)
  private set storageV2Ref(value: GridRegistrationFormStorageV2Component | undefined) {
    this.storageV2 = value ?? null;
    this.applyWhenReady();
  }

  @ViewChild(GridRegistrationFormConsumerComponent)
  private set consumerRef(value: GridRegistrationFormConsumerComponent | undefined) {
    this.consumer = value ?? null;
    this.applyWhenReady();
  }

  readonly storageVariant$ = combineLatest([this.dataShareService.tenantConfig$, this.model$]).pipe(
    map(([c, m]) => storageFormVariant(m, c)),
    filter((variant): variant is StorageFormVariant => variant != null),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly resolvedModel$ = this.model$.pipe(
    filter((model): model is GridRegistrationModel => model != null),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  constructor(
    protected authorizationService: AuthorizationService,
    protected dataShareService: DataShareService,
    private readonly fb: FormBuilder
  ) {
    super(authorizationService, dataShareService);
    this.placeholderForm = this.fb.group({});

    merge(
      this.model$.pipe(filter((m): m is GridRegistrationModel => m != null)),
      this.dataShareService.tenantConfig$
    )
      .pipe(debounceTime(0), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.applyWhenReady());
  }

  getFormGroup(): FormGroup {
    return this.getChild()?.getFormGroup() ?? this.placeholderForm;
  }

  isValid(): boolean {
    return this.getChild()?.isValid() ?? false;
  }

  loadErrorsList() {
    this.getChild()?.loadErrorsList();
  }

  getModelFromForm(): GridRegistrationModel {
    return this.getChild()!.getModelFromForm();
  }

  loadFormWithModel(result: GridRegistrationModel) {
    this.lastModel = result;
    this.lastOp = 'load';
    this.model$.next(result);
    this.applyWhenReady();
  }

  updateFormWithModel(result: GridRegistrationModel) {
    this.lastModel = result;
    this.lastOp = 'update';
    this.model$.next(result);
    this.applyWhenReady();
  }

  private getChild(): GridRegistrationFormBase | null {
    return this.storageV2 ?? this.consumer ?? null;
  }

  private applyWhenReady() {
    if (!this.lastModel) {
      return;
    }
    const child = this.getChild();
    if (!child) {
      return;
    }
    (child as GridRegistrationFormConsumerComponent | GridRegistrationFormStorageV2Component).originalModel =
      this.lastModel;
    if (this.lastChild !== child) {
      this.lastChild = child;
      child.loadFormWithModel(this.lastModel);
    } else if (this.lastOp === 'load') {
      child.loadFormWithModel(this.lastModel);
    } else {
      child.updateFormWithModel(this.lastModel);
    }
    this.wireChildErrors(child);
  }

  private wireChildErrors(child: GridRegistrationFormBase) {
    this.childErrorsSub?.unsubscribe();
    this.childErrorsSub = child.onErrorsChanged.subscribe((x) => this.onErrorsChanged.emit(x));
  }

  ngAfterViewInit() {
    this.applyWhenReady();
  }

  ngOnDestroy() {
    this.childErrorsSub?.unsubscribe();
  }
}
